// components/common/MapCameraController.tsx - VERSÃO COMPLETA COM TRACKING DE POSIÇÃO

import { useMap } from '@vis.gl/react-google-maps';
import React, { useEffect, useState } from 'react';

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  color?: string;
  icon?: string;
  type?: 'current' | 'sign' | 'image';
  imageUrl?: string;
  isSelected?: boolean;
  symbolName?: string;
  isOverlay?: boolean;
  projectName?: string;
  distance?: number;
  serviceId?: number;
}

interface MapCameraControllerProps {
  markers: MapMarker[];
  selectedSignMarkerId?: string | null;
  currentImageMarker?: MapMarker | null;
  followCurrentPosition?: boolean;
  autoZoom?: boolean;
  targetZoom?: number;
  smoothPan?: boolean;
}

const MapCameraController: React.FC<MapCameraControllerProps> = ({
  markers,
  selectedSignMarkerId,
  currentImageMarker = null,
  followCurrentPosition = true,
  autoZoom = true,
  targetZoom = 17,
  smoothPan = true
}) => {
  const map = useMap();
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [lastCurrentImageId, setLastCurrentImageId] = useState<string | null>(
    null
  );
  const [userMovedMap, setUserMovedMap] = useState(false);

  // ✅ Detectar quando usuário move o mapa manualmente
  useEffect(() => {
    if (!map) return;

    const handleDragEnd = () => {
      console.log('🖱️ [MapCamera] Usuário moveu o mapa manualmente');
      setUserMovedMap(true);
    };

    const listener = map.addListener('dragend', handleDragEnd);

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map]);

  // ✅ Resetar flag quando muda de marker ou imagem
  useEffect(() => {
    if (
      selectedSignMarkerId !== lastSelectedId ||
      currentImageMarker?.id !== lastCurrentImageId
    ) {
      setUserMovedMap(false);
    }
  }, [
    selectedSignMarkerId,
    currentImageMarker,
    lastSelectedId,
    lastCurrentImageId
  ]);

  // ✅ NOVO: Seguir posição atual da imagem (quando não há marker selecionado)
  useEffect(() => {
    if (!map || !followCurrentPosition || !currentImageMarker) {
      return;
    }

    // Se há um sign selecionado, não seguir a imagem (prioridade ao sign)
    if (selectedSignMarkerId) {
      return;
    }

    // Evitar re-centralização na mesma imagem
    if (currentImageMarker.id === lastCurrentImageId) {
      return;
    }

    // Se usuário moveu manualmente, não forçar centralização
    if (userMovedMap) {
      console.log(
        '⚠️ [MapCamera] Usuário moveu o mapa, ignorando auto-follow da imagem'
      );
      setLastCurrentImageId(currentImageMarker.id);
      return;
    }

    console.log('🎥 [MapCamera] Seguindo posição atual da imagem:', {
      imageId: currentImageMarker.id,
      title: currentImageMarker.title,
      lat: currentImageMarker.lat,
      lng: currentImageMarker.lng
    });

    // Centralizar na posição atual
    if (smoothPan) {
      map.panTo({ lat: currentImageMarker.lat, lng: currentImageMarker.lng });
    } else {
      map.setCenter({
        lat: currentImageMarker.lat,
        lng: currentImageMarker.lng
      });
    }

    setLastCurrentImageId(currentImageMarker.id);
  }, [
    map,
    currentImageMarker,
    followCurrentPosition,
    selectedSignMarkerId,
    lastCurrentImageId,
    userMovedMap,
    smoothPan
  ]);

  // ✅ Seguir marker selecionado (tem prioridade sobre seguir imagem)
  useEffect(() => {
    if (!map || !selectedSignMarkerId) {
      return;
    }

    // Evitar re-centralização no mesmo marker
    if (selectedSignMarkerId === lastSelectedId) {
      return;
    }

    // Se usuário moveu manualmente, não forçar centralização
    if (userMovedMap) {
      console.log(
        '⚠️ [MapCamera] Usuário moveu o mapa, ignorando auto-centralização no marker'
      );
      setLastSelectedId(selectedSignMarkerId);
      return;
    }

    const selectedMarker = markers.find(m => m.id === selectedSignMarkerId);

    if (!selectedMarker) {
      console.warn(
        '⚠️ [MapCamera] Marker não encontrado:',
        selectedSignMarkerId
      );
      return;
    }

    console.log('🎯 [MapCamera] Centralizando em marker selecionado:', {
      markerId: selectedMarker.id,
      title: selectedMarker.title,
      lat: selectedMarker.lat,
      lng: selectedMarker.lng
    });

    // Centralizar no marker
    if (smoothPan) {
      map.panTo({ lat: selectedMarker.lat, lng: selectedMarker.lng });
    } else {
      map.setCenter({ lat: selectedMarker.lat, lng: selectedMarker.lng });
    }

    // Ajustar zoom se necessário
    if (autoZoom) {
      const currentZoom = map.getZoom() || 15;

      if (currentZoom < targetZoom - 1) {
        console.log(
          `📹 [MapCamera] Ajustando zoom: ${currentZoom} → ${targetZoom}`
        );
        setTimeout(() => {
          map.setZoom(targetZoom);
        }, 300);
      }
    }

    setLastSelectedId(selectedSignMarkerId);
    // ✅ Limpar o tracking da imagem quando selecionar um marker
    setLastCurrentImageId(null);
  }, [
    map,
    markers,
    selectedSignMarkerId,
    lastSelectedId,
    userMovedMap,
    autoZoom,
    targetZoom,
    smoothPan
  ]);

  return null;
};

export default MapCameraController;
